import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-lg text-sm font-medium ring-offset-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] dark:ring-offset-background-dark",
  {
    variants: {
      variant: {
        default: "bg-gradient-primary text-white hover:shadow-md hover:translate-y-[-1px]",
        destructive: "bg-gradient-error text-white hover:shadow-md hover:translate-y-[-1px]",
        outline: "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:bg-opacity-10 hover:shadow-sm dark:text-primary-light dark:border-primary-light dark:hover:bg-primary-dark dark:hover:bg-opacity-20",
        secondary: "bg-gradient-secondary text-white hover:shadow-md hover:translate-y-[-1px]",
        ghost: "hover:bg-primary hover:bg-opacity-10 hover:text-primary dark:hover:bg-primary-dark dark:hover:bg-opacity-20 dark:hover:text-primary-light",
        link: "text-primary underline-offset-4 hover:underline dark:text-primary-light",
        glass: "glass text-primary shadow-sm hover:shadow-md hover:translate-y-[-1px] dark:text-primary-light",
        neumorphic: "neumorphic text-primary hover:scale-[1.02] transition-transform dark:text-primary-light",
        accent: "bg-gradient-accent text-white hover:shadow-md hover:translate-y-[-1px]",
        success: "bg-gradient-success text-white hover:shadow-md hover:translate-y-[-1px]",
      },
      size: {
        default: "h-11 px-5 py-2.5",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-11 w-11",
      },
      animation: {
        none: "",
        pulse: "animate-pulse",
        bounce: "animate-bounce-gentle",
        shimmer: "overflow-hidden relative before:absolute before:inset-0 before:bg-shimmer before:translate-x-[-100%] before:animate-shimmer",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      animation: "none",
    },
  }
)

const Button = React.forwardRef(
  ({ className, variant, size, animation, asChild = false, ...props }, ref) => {
    const Comp = asChild ? React.Fragment : "button"
    
    if (asChild) {
      // When using as a Fragment, don't pass className
      const { children, ...restProps } = props;
      return <Comp ref={ref} {...restProps}>{children}</Comp>;
    }
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, animation, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
